import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { cloneDeep } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { baseUrl } from "@/consts";
import { Product, ProductType, Supplier } from "@/interfaces";
import { useDataApi } from "@/hooks";
import {
  ErrorMessage,
  ImageCentered,
  UploadFileInput,
} from "@/components/atoms";
import {
  colors,
  sxButton,
  sxButtonBase,
  sxFont_14_500,
  sxFont_18_500,
} from "@/theme";
import {
  formatAxiosError,
  getBoolean,
  getFloat,
  getInt,
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString,
  isNumber,
  isStringEqualCI,
  reMoney,
  reInt,
  formatDateToPST,
  formatName,
  unique,
} from "@/utils";
import {
  ProductHelper,
  SupplierHelper,
  MuiHelper,
  FormikHelper,
  YupHelper,
} from "@/helpers";
import PlaceholderSVG from "@/assets/placeholder-svg.svg";

export type ProductEditDialogProps = {
  product?: Product;
  suppliers?: Supplier[];
  productTypes?: ProductType[];
  open?: boolean;
  onClose?: (product?: Product) => void;
};

const validationSchema = Yup.object().shape({
  sku: Yup.string()
    .required("SKU is required")
    .test("checkDupSKU", "SKU already exists", (value, context) =>
      YupHelper.testDuplicate(
        value,
        context,
        `${baseUrl.PRODUCT}?search=`,
        "productId",
        "sku"
      )
    ),
  price: Yup.number().required("Price is required"),
  productDescription: Yup.string().required("Product description is required"),
  productImageUrl: Yup.string().optional().nullable(),
  mediaId: Yup.string().required("Media ID is required"),
  inventoryCount: Yup.number().required("Inventory count is required"),
  offsite: Yup.boolean().required("Fulfillment is required"),
  supplierId: Yup.number().required("Supplier is required"),
  productTypeId: Yup.number().required("Product type is required"),
});

const initialValues = {
  productId: undefined as unknown as number,
  sku: undefined,
  price: undefined,
  productDescription: undefined,
  productImageUrl: undefined,
  inventoryCount: undefined,
  mediaId: undefined,
  offsite: false,
  supplierId: undefined,
  productTypeId: undefined,
};

const selectMenuInDialogProps = {
  disablePortal: true,
} as const;

const ProductEditDialog: React.FC<ProductEditDialogProps> = ({
  product: _product,
  suppliers,
  productTypes,
  open = false,
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const supplierOptions: Supplier[] = Array.isArray(suppliers)
    ? suppliers
    : (suppliers as { data?: Supplier[] } | undefined)?.data ?? [];

  const { post, put, error } = useDataApi(baseUrl.PRODUCT);

  const { errors, touched, handleBlur, ...formik } = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: cloneDeep(
      isNumber(_product?.productId)
        ? {
          ..._product,
          ...{ price: (_product as Product)?.price?.toString() },
        }
        : {
          ...initialValues,
          ...{ createdBy: (_product as Product)?.createdBy },
          ...(isNumber((_product as Product)?.supplierId)
            ? { supplierId: (_product as Product).supplierId }
            : {}),
          ...(isNumber((_product as Product)?.productTypeId)
            ? { productTypeId: (_product as Product).productTypeId }
            : {}),
        }
    ),
    validateOnMount: false,
    onSubmit(values, { validateForm }) {
      validateForm(values).then(() =>
        onConfirmButtonClick({
          ...values,
          ...{ price: getFloat(values.price) },
        } as Product)
      );
    },
  });

  useEffect(() => {
    if (error?.config?.url?.indexOf(baseUrl.PRODUCT) >= 0) {
      setErrorMessage(formatAxiosError(error));
    }
  }, [error]);

  const onConfirmButtonClick = async (product?: Product) => {
    setErrorMessage(undefined);

    if (!product || !formik.dirty) {
      onClose?.();
      return;
    }

    let _error = undefined;

    try {
      await validationSchema.validate(product);

      if (!isNumber(product.productId)) {
        await post?.(baseUrl.PRODUCT, product);
      } else {
        await put?.(`${baseUrl.PRODUCT}/${product.productId}`, product);
      }
    } catch (e) {
      _error = true;

      if (e instanceof Yup.ValidationError) {
        if (isNonEmptyArray(e.errors)) {
          setErrorMessage(e.errors[0]);
        } else {
          setErrorMessage("Unknown validation error");
          console.log(e);
        }
      } else {
        console.log(e);
      }
    }

    if (error || _error) {
      return;
    }

    onClose?.(product);
  };

  const handleClose = (_: any, reason: string) => {
    if (isStringEqualCI(reason, "backdropClick")) {
      return;
    }

    onConfirmButtonClick();
  };

  const processUploadResult = (result?: any) => {
    if (isNonEmptyArray(result?.data)) {
      formik.setFieldValue("productImageUrl", result?.data?.[0]);
    }

    if (isNonEmptyString(result?.data)) {
      setErrorMessage(result?.data);
    }
  };

  const processUploadError = (error?: any) => {
    if (isNonEmptyObject(error)) {
      setErrorMessage(formatAxiosError(error));
    }

    if (error == null) {
      setErrorMessage(undefined);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <Typography sx={{ ...sxFont_18_500, textAlign: "center" }}>{`${isNumber(_product?.productId) ? "Product Information" : "Add Product"
          } `}</Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleClose(undefined, undefined as unknown as string)}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: colors.white,
          backgroundColor: colors.primaryOzone500,
          "&:hover": {
            color: colors.white,
            background: colors.primaryOzone500,
          },
        }}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            padding: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection:'column',
              gap: "20px",
              paddingRight: "5px",
            }}
          >
            <Box
              sx={{
                width: "300px",
                height:'300px',
                // aspectRatio: "1 / 1",

                background: "#F3F4F6",
                border: "2px solid #E5E7EB",
                borderRadius: "10px",
                display: "flex",
                // alignItems: "center",
                // justifyContent: "center",
                // overflow: "hidden",
                // position: "relative",
              }}
            >
              {formik.values.productImageUrl ? (
                <img
                  src={formik.values.productImageUrl}
                  alt={formik.values.productDescription}
                  title={formik.values.productDescription}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: "10px",
                    // display: "block",
                  }}
                />
              ) : (
                <img
                  src={PlaceholderSVG}
                  alt="placeholder"
                  style={{
                    width: "98%",
                    height: "98%",
                    opacity: 1,
                  }}
                />
              )}
            </Box>
            <UploadFileInput
              url={`${baseUrl.BLOB_STORAGE}/upload`}
              text="Upload Image"
              additionalFormData={{ Source: "Product" }}
              onUploaded={(result) => processUploadResult(result)}
              onError={(error) => processUploadError(error)}
              sxContainer={{ placeSelf: "center stretch",width: "100%", }}
              sxButton={{
                ...sxButtonBase,
                width: "100%",
                padding: '16px 32px',
                border: `1px solid ${colors.primaryOzone500}`,
                color: colors.primaryOzone500,
                borderRadius: '10px',
                ...sxFont_14_500,
              }}
            />
            <TextField
              id="productImageUrl"
              name=""
              placeholder="https://"
              label="Image URL"
              value={formik.values.productImageUrl}
              onChange={({ target: { value } }) =>
                formik.setFieldValue("productImageUrl", value)
              }
              onBlur={handleBlur}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              rowGap: "10px",
              paddingLeft: "5px",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: "10px",
              }}
            >
              <FormControl fullWidth>
                <Select
                  id="supplierId"
                  name="supplierId"
                  displayEmpty
                  required
                  value={formik.values.supplierId?.toString() || ""}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "supplierId",
                      formik.setFieldValue,
                      getInt
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.supplierId) && touched.supplierId
                  }
                  fullWidth
                  MenuProps={selectMenuInDialogProps}
                >
                  <MenuItem value="" disabled>
                    <em>Supplier</em>
                  </MenuItem>
                  {MuiHelper.getMenuItems(
                    unique(supplierOptions, SupplierHelper.isSupplierEqual),
                    "supplierId",
                    "supplierName"
                  )}
                </Select>
                {isNonEmptyString(errors.supplierId) ? (
                  <FormHelperText>{errors.supplierId}</FormHelperText>
                ) : undefined}
              </FormControl>
              <FormControl fullWidth>
                <Select
                  id="productTypeId"
                  name="productTypeId"
                  displayEmpty
                  required
                  value={formik.values.productTypeId?.toString() || ""}
                  onChange={({ target: { value } }) =>
                    FormikHelper.handleNumberChange(
                      value,
                      "productTypeId",
                      formik.setFieldValue,
                      getInt
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    isNonEmptyString(errors.productTypeId) &&
                    touched.productTypeId
                  }
                  fullWidth
                  MenuProps={selectMenuInDialogProps}
                >
                  <MenuItem value="" disabled>
                    <em>Product Type</em>
                  </MenuItem>
                  {MuiHelper.getMenuItems(
                    productTypes,
                    "productTypeId",
                    "productTypeName"
                  )}
                </Select>
                {isNonEmptyString(errors.productTypeId) ? (
                  <FormHelperText>{errors.productTypeId}</FormHelperText>
                ) : undefined}
              </FormControl>
            </Box>
            <TextField
              id="sku"
              name="sku"
              placeholder="SKU"
              required
              value={formik.values.sku}
              onChange={({ target: { value } }) =>
                formik.setFieldValue("sku", value)
              }
              onBlur={handleBlur}
              error={isNonEmptyString(errors.sku) && touched.sku}
              helperText={errors.sku}
              fullWidth
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">#</InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              id="productDescription"
              name="productDescription"
              placeholder="Product Name*"
              required
              value={formik.values.productDescription}
              onChange={({ target: { value } }) =>
                formik.setFieldValue("productDescription", value)
              }
              onBlur={handleBlur}
              error={
                isNonEmptyString(errors.productDescription) &&
                touched.productDescription
              }
              helperText={errors.productDescription}
              fullWidth
              variant="outlined"
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: "10px",
              }}
            >
              <TextField
                id="inventoryCount"
                name="inventoryCount"
                placeholder="Inventory Qty*"
                required
                value={formik.values.inventoryCount}
                onChange={({ target: { value } }) =>
                  FormikHelper.handleNumberChange(
                    value,
                    "inventoryCount",
                    formik.setFieldValue,
                    getInt,
                    reInt
                  )
                }
                onBlur={handleBlur}
                error={
                  isNonEmptyString(errors.inventoryCount) &&
                  touched.inventoryCount
                }
                helperText={errors.inventoryCount}
                fullWidth
                variant="outlined"
              />
              <TextField
                id="price"
                name="price"
                placeholder="Price*"
                required
                value={formik.values.price}
                onChange={({ target: { value } }) =>
                  FormikHelper.handleNumberChange(
                    value,
                    "price",
                    formik.setFieldValue,
                    undefined,
                    reMoney
                  )
                }
                onBlur={handleBlur}
                error={isNonEmptyString(errors.price) && touched.price}
                helperText={errors.price}
                fullWidth
                variant="outlined"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: "10px",
                alignItems: "center",
              }}
            >
              <TextField
                id="mediaId"
                name="mediaId"
                placeholder="Media ID*"
                required
                value={formik.values.mediaId}
                onChange={({ target: { value } }) =>
                  formik.setFieldValue("mediaId", value)
                }
                onBlur={handleBlur}
                error={isNonEmptyString(errors.mediaId) && touched.mediaId}
                helperText={errors.mediaId}
                fullWidth
                variant="outlined"
              />
              <FormControl>
                <RadioGroup
                  row
                  id="offsite"
                  name="offsite"
                  value={formik.values.offsite?.toString()}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue("offsite", getBoolean(value))
                  }
                  onBlur={handleBlur}
                >
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label={ProductHelper.FULFILLMENT_IN_STORE_STRING}
                  />
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label={ProductHelper.FULFILLMENT_OFF_SITE_STRING}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box sx={{display:'flex', flexDirection:'row',alignItems:'center', mb:7 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
                  <Box>
                    <Typography>Created by</Typography>
                  </Box>
                  <Box>
                    <Typography>
                      {formatName(
                        _product?.creatorFirstName,
                        _product?.creatorLastName
                      ) || _product?.createdBy}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
                  <Box>
                    <Typography>Created on</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <Typography>
                      {formatDateToPST(_product?.createdOn as Date)}
                    </Typography>

                  </Box>
                </Box>

              </Box>
              <Button
                onClick={() =>
                  onConfirmButtonClick({
                    ...formik.values,
                    ...{ price: getFloat(formik.values.price) },
                  } as Product)
                }
                autoFocus
                sx={sxButton}
                style={{ marginLeft: "auto" }}
              >
                {`${isNumber(_product?.productId) ? "Save" : "Add Product"} `}
              </Button>
            </Box>
          </Box>
        </Box>
        {!!errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </DialogContent>
    </Dialog>
  );
};

export default ProductEditDialog;
